<template>
  <div class="fc-root">

    <!-- ── Topbar ─────────────────────────────────────────────────── -->
    <div class="fc-topbar">
      <div>
        <div class="fc-topbar__title white--text">Flujo de caja del período</div>
        <div class="fc-topbar__sub">Consolidado de entradas y salidas · {{ periodoLabel }}</div>
      </div>
      <div class="fc-topbar__filters">
        <DatePickerMultiple @process-select="setDateFilter" />
        <SucursalesVFilter />
      </div>
    </div>

    <!-- ── Skeleton ───────────────────────────────────────────────── -->
    <template v-if="loading">
      <div class="fc-kpi-row">
        <v-skeleton-loader v-for="n in 4" :key="n" dark type="card" height="96" />
      </div>
      <div class="fc-detail-grid">
        <v-skeleton-loader v-for="n in 3" :key="'d'+n" dark type="list-item-three-line@4" />
      </div>
    </template>

    <template v-else>

      <!-- ── KPI cards ──────────────────────────────────────────── -->
      <div class="fc-kpi-row">

        <div class="fc-kpi fc-kpi--ventas">
          <div class="fc-kpi__label">Ventas</div>
          <div class="fc-kpi__value">{{ totalVentas | convertPesoArg }}</div>
          <div class="fc-kpi__meta">
            <v-icon x-small class="mr-1" color="rgba(255,255,255,0.35)">mdi-arrow-down-thin</v-icon>
            Ingresos del período
          </div>
        </div>

        <div class="fc-kpi fc-kpi--compras">
          <div class="fc-kpi__label">Compras</div>
          <div class="fc-kpi__value">{{ totalCompras | convertPesoArg }}</div>
          <div class="fc-kpi__meta">
            <v-icon x-small class="mr-1" color="rgba(255,255,255,0.35)">mdi-arrow-up-thin</v-icon>
            Pagos a proveedores
          </div>
        </div>

        <div class="fc-kpi fc-kpi--gastos">
          <div class="fc-kpi__label">Gastos operativos</div>
          <div class="fc-kpi__value">{{ totalGastos | convertPesoArg }}</div>
          <div class="fc-kpi__meta">
            <v-icon x-small class="mr-1" color="rgba(255,255,255,0.35)">mdi-arrow-up-thin</v-icon>
            Egresos del período
          </div>
        </div>

        <div :class="['fc-kpi', resultado >= 0 ? 'fc-kpi--pos' : 'fc-kpi--neg']">
          <div class="fc-kpi__label">Resultado neto</div>
          <div class="fc-kpi__value fc-kpi__value--result">{{ resultado | convertPesoArg }}</div>
          <div class="fc-kpi__meta">
            <v-icon x-small class="mr-1" color="rgba(255,255,255,0.35)">
              {{ resultado >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}
            </v-icon>
            {{ resultado >= 0 ? 'Flujo positivo' : 'Flujo negativo' }}
          </div>
        </div>

      </div>

      <!-- ── Detalle ─────────────────────────────────────────────── -->
      <div class="fc-detail-grid">

        <!-- Ventas -->
        <div class="fc-panel">
          <div class="fc-panel__head fc-panel__head--ventas">
            <v-icon small class="mr-2" color="rgba(255,255,255,0.5)">mdi-cash-multiple</v-icon>
            <span>Forma de pago</span>
          </div>
          <div class="fc-panel__body">
            <div v-if="detalleVentas.length === 0" class="fc-empty">Sin movimientos en el período</div>
            <div v-for="(item, i) in detalleVentas" :key="i" class="fc-row">
              <div class="fc-row__left">
                <span class="fc-row__rank">{{ i + 1 }}</span>
                <div class="fc-row__label-wrap">
                  <span class="fc-row__name">{{ item.forma_pago }}</span>
                  <div class="fc-bar">
                    <div class="fc-bar__fill fc-bar__fill--ventas" :style="{ width: pct(item.total, totalVentas) + '%' }" />
                  </div>
                </div>
              </div>
              <div class="fc-row__right">
                <span class="fc-row__amount">{{ item.total | convertPesoArg }}</span>
                <span class="fc-row__pct">{{ pct(item.total, totalVentas) | pctFmt }}%</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Compras -->
        <div class="fc-panel">
          <div class="fc-panel__head fc-panel__head--compras">
            <v-icon small class="mr-2" color="rgba(255,255,255,0.5)">mdi-package-variant</v-icon>
            <span>Proveedor</span>
          </div>
          <div class="fc-panel__body">
            <div v-if="detalleCompras.length === 0" class="fc-empty">Sin movimientos en el período</div>
            <template v-for="(item, i) in detalleCompras">
              <div
                :key="'c' + i"
                :class="['fc-row', 'fc-row--clickable', { 'fc-row--open': compraAbierta === claveProveedor(item) }]"
                @click="toggleCompra(item)"
              >
                <div class="fc-row__left">
                  <span class="fc-row__rank">{{ i + 1 }}</span>
                  <div class="fc-row__label-wrap">
                    <span class="fc-row__name">{{ item.proveedor }}</span>
                    <div class="fc-bar">
                      <div class="fc-bar__fill fc-bar__fill--compras" :style="{ width: pct(item.total, totalCompras) + '%' }" />
                    </div>
                  </div>
                </div>
                <div class="fc-row__right">
                  <span class="fc-row__amount">{{ item.total | convertPesoArg }}</span>
                  <span class="fc-row__pct">
                    {{ pct(item.total, totalCompras) | pctFmt }}% · {{ item.cantidad }} {{ item.cantidad === 1 ? 'pago' : 'pagos' }}
                  </span>
                </div>
                <v-icon x-small class="fc-row__chevron" color="rgba(255,255,255,0.3)">
                  {{ compraAbierta === claveProveedor(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </div>
              <div v-if="compraAbierta === claveProveedor(item)" :key="'cd' + i" class="fc-sub">
                <div v-if="cargandoDetalle" class="fc-sub__loading">
                  <v-progress-circular indeterminate size="14" width="2" color="rgba(255,255,255,0.3)" />
                </div>
                <template v-else>
                  <div v-for="pago in comprasDetalle[claveProveedor(item)]" :key="pago.id" class="fc-sub__row">
                    <span class="fc-sub__fecha">{{ pago.fecha | fechaCorta }}</span>
                    <span class="fc-sub__desc">
                      {{ pago.comprobantes.length ? pago.comprobantes.join(', ') : 'Pago ' + pago.numero }}
                      <small v-if="pago.sucursal">· {{ pago.sucursal }}</small>
                    </span>
                    <span class="fc-sub__monto">{{ pago.total | convertPesoArg }}</span>
                  </div>
                </template>
              </div>
            </template>
          </div>
        </div>

        <!-- Gastos -->
        <div class="fc-panel">
          <div class="fc-panel__head fc-panel__head--gastos">
            <v-icon small class="mr-2" color="rgba(255,255,255,0.5)">mdi-receipt-text-outline</v-icon>
            <span>Tipo de gasto</span>
          </div>
          <div class="fc-panel__body">
            <div v-if="detalleGastos.length === 0" class="fc-empty">Sin movimientos en el período</div>
            <template v-for="(item, i) in detalleGastos">
              <div
                :key="'g' + i"
                :class="['fc-row', 'fc-row--clickable', { 'fc-row--open': gastoAbierto === item.tipo }]"
                @click="toggleGasto(item)"
              >
                <div class="fc-row__left">
                  <span class="fc-row__rank">{{ i + 1 }}</span>
                  <div class="fc-row__label-wrap">
                    <span class="fc-row__name">{{ item.tipo }}</span>
                    <div class="fc-bar">
                      <div class="fc-bar__fill fc-bar__fill--gastos" :style="{ width: pct(item.total, totalGastos) + '%' }" />
                    </div>
                  </div>
                </div>
                <div class="fc-row__right">
                  <span class="fc-row__amount">{{ item.total | convertPesoArg }}</span>
                  <span class="fc-row__pct">
                    {{ pct(item.total, totalGastos) | pctFmt }}% · {{ item.cantidad }} {{ item.cantidad === 1 ? 'gasto' : 'gastos' }}
                  </span>
                </div>
                <v-icon x-small class="fc-row__chevron" color="rgba(255,255,255,0.3)">
                  {{ gastoAbierto === item.tipo ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </div>
              <div v-if="gastoAbierto === item.tipo" :key="'gd' + i" class="fc-sub">
                <div v-if="cargandoDetalle" class="fc-sub__loading">
                  <v-progress-circular indeterminate size="14" width="2" color="rgba(255,255,255,0.3)" />
                </div>
                <template v-else>
                  <div v-for="gasto in gastosDetalle[item.tipo]" :key="gasto.id" class="fc-sub__row">
                    <span class="fc-sub__fecha">{{ gasto.fecha | fechaCorta }}</span>
                    <span class="fc-sub__desc">
                      {{ gasto.descripcion || gasto.categoria || item.tipo }}
                      <small v-if="gasto.sucursal">· {{ gasto.sucursal }}</small>
                    </span>
                    <span class="fc-sub__monto">{{ gasto.monto | convertPesoArg }}</span>
                  </div>
                </template>
              </div>
            </template>
          </div>
        </div>

      </div>

      <!-- ── Nota ──────────────────────────────────────────────────── -->
      <div class="fc-disclaimer">
        <v-icon x-small class="mr-1" style="opacity:.4">mdi-information-outline</v-icon>
        El resultado refleja el flujo de efectivo del período, no la ganancia contable.
        No considera stock ni costo de mercadería vendida.
        Las compras incluyen solo pagos efectivamente realizados en el período.
      </div>

    </template>
  </div>
</template>

<script>
import DatePickerMultiple from "@/components/reportes/DatePickerMultiple";
import SucursalesVFilter from "@/components/sucursal/SucursalesVFilter.vue";

export default {
  name: "ReporteFlujoCaja",
  components: { DatePickerMultiple, SucursalesVFilter },
  filters: {
    pctFmt(val) {
      return Number(val).toFixed(1);
    },
    // 'AAAA-MM-DD' → 'DD/MM' sin pasar por Date (evita el corrimiento UTC de un día)
    fechaCorta(val) {
      if (!val) return "";
      const [, m, d] = val.split("-");
      return `${d}/${m}`;
    },
  },
  data() {
    return {
      dateFilter: null,
      loading: false,
      totalVentas: 0,
      totalCompras: 0,
      totalGastos: 0,
      resultado: 0,
      detalleVentas: [],
      detalleCompras: [],
      detalleGastos: [],
      // Drill-down: qué fila está abierta y el detalle ya traído (cache por clave)
      gastoAbierto: null,
      compraAbierta: null,
      gastosDetalle: {},
      comprasDetalle: {},
      cargandoDetalle: false,
    };
  },
  computed: {
    periodoLabel() {
      if (!this.dateFilter) return "—";
      const params = new URLSearchParams(this.dateFilter);
      const desde = params.get("from_current");
      const hasta = params.get("to_current");
      if (desde && hasta) return `${desde} al ${hasta}`;
      return "—";
    },
  },
  methods: {
    setDateFilter(filter) {
      this.$router.replace({ query: { ...this.$route.query, date: filter } });
      this.dateFilter = filter;
    },
    pct(valor, total) {
      if (!total) return 0;
      return (valor / total) * 100;
    },
    filtrosBase() {
      const rawDate = this.dateFilter || this.$route.query.date || "";
      const params = new URLSearchParams(rawDate);
      const desde = params.get("from_current");
      const hasta = params.get("to_current");
      if (!desde || !hasta) return null;
      const sucursal = this.$route.query.uuid_sucursal;
      let qs = `desde=${desde}&hasta=${hasta}`;
      if (sucursal && sucursal !== "all") qs += `&sucursal=${sucursal}`;
      return qs;
    },
    // Los pagos sin proveedor vienen con proveedor_id null; el backend los pide como 0
    claveProveedor(item) {
      return item.proveedor_id || 0;
    },
    // El tipo de gasto es por sucursal (mismo nombre, distinto id): el reporte
    // agrupa por nombre y el detalle también se pide por nombre
    toggleGasto(item) {
      if (this.gastoAbierto === item.tipo) { this.gastoAbierto = null; return; }
      this.gastoAbierto = item.tipo;
      if (this.gastosDetalle[item.tipo]) return;
      this.cargarDetalle(`/reportes/flujo-caja/gastos?${this.filtrosBase()}&tipo=${encodeURIComponent(item.tipo)}`, "gastosDetalle", item.tipo);
    },
    toggleCompra(item) {
      const clave = this.claveProveedor(item);
      if (this.compraAbierta === clave) { this.compraAbierta = null; return; }
      this.compraAbierta = clave;
      if (this.comprasDetalle[clave]) return;
      this.cargarDetalle(`/reportes/flujo-caja/compras?${this.filtrosBase()}&proveedor=${clave}`, "comprasDetalle", clave);
    },
    cargarDetalle(url, cache, clave) {
      this.cargandoDetalle = true;
      this.axios
        .get(url)
        .then(({ data }) => { this.$set(this[cache], clave, data); })
        .catch(() => { this.$set(this[cache], clave, []); })
        .finally(() => { this.cargandoDetalle = false; });
    },
    fetchResumen() {
      const rawDate = this.dateFilter || this.$route.query.date || "";
      if (!rawDate) return;
      const params = new URLSearchParams(rawDate);
      const desde = params.get("from_current");
      const hasta = params.get("to_current");
      if (!desde || !hasta) return;

      const sucursal = this.$route.query.uuid_sucursal;
      let url = `/reportes/flujo-caja?desde=${desde}&hasta=${hasta}`;
      if (sucursal && sucursal !== "all") url += `&sucursal=${sucursal}`;

      this.loading = true;
      // Cambió el período o la sucursal: el detalle abierto ya no corresponde
      this.gastoAbierto = this.compraAbierta = null;
      this.gastosDetalle = {};
      this.comprasDetalle = {};
      this.axios
        .get(url)
        .then(({ data }) => {
          this.totalVentas  = data.total_ventas  || 0;
          this.totalCompras = data.total_compras || 0;
          this.totalGastos  = data.total_gastos  || 0;
          this.resultado    = data.resultado     || 0;
          this.detalleVentas  = data.detalle_ventas  || [];
          this.detalleCompras = data.detalle_compras || [];
          this.detalleGastos  = data.detalle_gastos  || [];
        })
        .catch(() => {
          this.totalVentas = this.totalCompras = this.totalGastos = this.resultado = 0;
          this.detalleVentas = this.detalleCompras = this.detalleGastos = [];
        })
        .finally(() => { this.loading = false; });
    },
  },
  created() {
    const urlDate = this.$route.query.date;
    if (urlDate) this.dateFilter = urlDate;
    else this.$router.replace({ path: this.$route.path, query: {} });
  },
  watch: {
    dateFilter(val) { if (val) this.fetchResumen(); },
    "$route.query.uuid_sucursal": {
      immediate: true,
      handler(v) { if (v) this.fetchResumen(); },
    },
  },
};
</script>

<style scoped>
/* ── Variables dark ───────────────────────────────────────────────── */
/* superficie base Vuetify dark: ~#1E1E1E                             */
/* card elevada:                  ~#272727                            */
/* borde sutil:                   rgba(255,255,255,0.08)              */
/* texto primario:                rgba(255,255,255,0.87)              */
/* texto secundario:              rgba(255,255,255,0.5)               */

.fc-root {
  padding: 24px 28px;
  max-width: 1140px;
}

/* ── Topbar ───────────────────────────────────────────────────────── */
.fc-topbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}
.fc-topbar__title {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}
.fc-topbar__sub {
  font-size: 12px;
  color: rgba(255,255,255,0.4);
  margin-top: 3px;
}
.fc-topbar__filters {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

/* ── KPI row ──────────────────────────────────────────────────────── */
.fc-kpi-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.fc-kpi {
  background: #272727;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
  padding: 18px 20px;
  border-top: 3px solid transparent;
}
.fc-kpi--ventas  { border-top-color: #4caf50; }
.fc-kpi--compras { border-top-color: #42a5f5; }
.fc-kpi--gastos  { border-top-color: #ffa726; }
.fc-kpi--pos     { border-top-color: #26a69a; }
.fc-kpi--neg     { border-top-color: #ef5350; }

.fc-kpi__label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: 600;
  color: rgba(255,255,255,0.4);
  margin-bottom: 10px;
}
.fc-kpi__value {
  font-size: 22px;
  font-weight: 700;
  color: rgba(255,255,255,0.87);
  line-height: 1.2;
  margin-bottom: 10px;
}
.fc-kpi--pos .fc-kpi__value--result { color: #4db6ac; }
.fc-kpi--neg .fc-kpi__value--result { color: #ef9a9a; }

.fc-kpi__meta {
  font-size: 11px;
  color: rgba(255,255,255,0.3);
  display: flex;
  align-items: center;
}

/* ── Paneles detalle ──────────────────────────────────────────────── */
.fc-detail-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.fc-panel {
  background: #272727;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
  overflow: hidden;
}

.fc-panel__head {
  display: flex;
  align-items: center;
  padding: 11px 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255,255,255,0.4);
  border-bottom: 1px solid rgba(255,255,255,0.07);
  background: rgba(255,255,255,0.03);
}
.fc-panel__head--ventas  { border-left: 3px solid #4caf50; }
.fc-panel__head--compras { border-left: 3px solid #42a5f5; }
.fc-panel__head--gastos  { border-left: 3px solid #ffa726; }

/* ── Filas de detalle ─ */
.fc-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.fc-row:last-child { border-bottom: none; }

.fc-row__left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.fc-row__rank {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: rgba(255,255,255,0.06);
  font-size: 10px;
  font-weight: 700;
  color: rgba(255,255,255,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}

.fc-row__label-wrap {
  flex: 1;
  min-width: 0;
}

.fc-row__name {
  display: block;
  font-size: 13px;
  color: rgba(255,255,255,0.75);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 5px;
}

.fc-bar {
  height: 3px;
  background: rgba(255,255,255,0.07);
  border-radius: 2px;
  overflow: hidden;
}
.fc-bar__fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}
.fc-bar__fill--ventas  { background: #4caf50; }
.fc-bar__fill--compras { background: #42a5f5; }
.fc-bar__fill--gastos  { background: #ffa726; }

.fc-row__right {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.fc-row__amount {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255,255,255,0.87);
  white-space: nowrap;
}

.fc-row__pct {
  font-size: 10px;
  color: rgba(255,255,255,0.25);
}

.fc-row--clickable { cursor: pointer; }
.fc-row--clickable:hover,
.fc-row--open { background: rgba(255,255,255,0.03); }
.fc-row__chevron { flex-shrink: 0; }

/* ── Detalle desplegado bajo una fila ─ */
.fc-sub {
  background: rgba(0,0,0,0.18);
  border-bottom: 1px solid rgba(255,255,255,0.05);
  padding: 4px 16px 6px 44px;
}
.fc-sub__loading {
  padding: 8px 0;
  text-align: center;
}
.fc-sub__row {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 5px 0;
  font-size: 12px;
  border-bottom: 1px dashed rgba(255,255,255,0.05);
}
.fc-sub__row:last-child { border-bottom: none; }
.fc-sub__fecha {
  flex-shrink: 0;
  width: 38px;
  color: rgba(255,255,255,0.35);
  font-variant-numeric: tabular-nums;
}
.fc-sub__desc {
  flex: 1;
  min-width: 0;
  color: rgba(255,255,255,0.65);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fc-sub__desc small { color: rgba(255,255,255,0.3); }
.fc-sub__monto {
  flex-shrink: 0;
  color: rgba(255,255,255,0.8);
  font-weight: 500;
  white-space: nowrap;
}

.fc-empty {
  padding: 28px 16px;
  text-align: center;
  font-size: 12px;
  color: rgba(255,255,255,0.2);
}

/* ── Disclaimer ───────────────────────────────────────────────────── */
.fc-disclaimer {
  font-size: 11px;
  color: rgba(255,255,255,0.25);
  display: flex;
  align-items: flex-start;
  line-height: 1.7;
}

/* ── Responsive ───────────────────────────────────────────────────── */
@media (max-width: 960px) {
  .fc-kpi-row     { grid-template-columns: repeat(2, 1fr); }
  .fc-detail-grid { grid-template-columns: 1fr; }

}
@media (max-width: 600px) {
  .fc-root        { padding: 16px; }
  .fc-topbar      { flex-direction: column; }
}
</style>
